import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';

interface TOCSection {
  id: string;
  label: string;
  icon?: string;
  bgGradient?: string;
}

interface TableOfContentButtonProps {
  sections: TOCSection[];
  scrollOffset?: number;
}

const TableOfContentButton: React.FC<TableOfContentButtonProps> = ({
  sections,
  scrollOffset = 96,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(
    sections[0]?.id || ''
  );

  const toggleToc = () => {
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !isOpen) {
      return;
    }

    const handleScroll = () => {
      const scrollPosition = window.scrollY + scrollOffset;
      let currentId = sections[0]?.id || '';

      for (const { id } of sections) {
        const element = document.getElementById(id);
        if (element) {
          const elementTop = element.offsetTop;
          if (scrollPosition >= elementTop) {
            currentId = id;
          }
        }
      }

      setActiveSection(prev => (prev === currentId ? prev : currentId));
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sections, scrollOffset, isOpen]);

  const handleScrollToSection = useCallback(
    (sectionId: string) => {
      if (typeof window === 'undefined') {
        return;
      }

      const element = document.getElementById(sectionId);
      if (!element) {
        return;
      }

      const elementTop =
        element.getBoundingClientRect().top + window.pageYOffset;
      const targetPosition = elementTop - scrollOffset;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
      });

      setActiveSection(sectionId);
      setIsOpen(false);
    },
    [scrollOffset]
  );

  return (
    <>
      {isOpen && (
        <div
          className='fixed inset-0 bg-black/70 z-[1000]'
          onClick={toggleToc}
          aria-hidden='true'
        />
      )}
      <div className='fixed right-3 bottom-[calc(5rem+4rem+0.5rem)] z-[1100]'>
        <button
          id='clip_table_of_content_button'
          onClick={toggleToc}
          className={`relative border-[#5c5c5c] ${
            isOpen ? 'bg-[#3c3c3c]' : 'bg-[#1c1c1c]'
          } hover:bg-[#5c5c5c] text-[#c6c6c6] border-2 shadow-lg rounded-full p-3 transition-all duration-300 ease-in-out`}
        >
          <Icon icon='mdi:table-of-contents' className='w-8 h-8' />
        </button>

        {isOpen && (
          <nav
            className='absolute bottom-full right-1 mb-2 bg-white border border-slate-200 rounded-xl px-3 shadow-[0_12px_30px_rgba(15,23,42,0.08)] max-h-[calc(100vh-180px)] overflow-y-auto w-60 lg:w-[250px] lg:p-4 lg:shadow-[0_8px_25px_rgba(15,23,42,0.15)]'
            aria-label='Weekly report sections'
          >
            <ul className='list-none m-0 p-0 flex flex-col'>
              {sections.map((section, index) => {
                const isActive = activeSection === section.id;
                return (
                  <li key={section.id} className='block'>
                    {index !== 0 && (
                      <div className='border-t border-slate-200' />
                    )}
                    <button
                      type='button'
                      className={`w-full bg-white text-left border-none rounded-lg py-3 cursor-pointer transition-all text-sm font-medium lg:text-[13px] flex items-center gap-3 ${
                        isActive ? ' text-black' : 'text-slate-800'
                      }`}
                      onClick={() => handleScrollToSection(section.id)}
                    >
                      {section.icon && (
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg flex-shrink-0 ${
                            section.bgGradient ||
                            'bg-gradient-to-br from-slate-100 to-slate-200'
                          }`}
                        >
                          {section.icon}
                        </div>
                      )}
                      {section.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </div>
    </>
  );
};

export default TableOfContentButton;
